import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  LogOut, Bell, Building, CheckCircle, Clock, FileText,
  ThumbsUp, ThumbsDown, Loader2, AlertTriangle, PoundSterling, Download,
  Settings, Save, Banknote, CreditCard, Shield, ChevronRight, Home,
  Zap, Flame, Calendar, User, MapPin, Phone, Mail, TrendingUp, 
  TrendingDown, Wrench, Eye, BedDouble, AlertCircle
} from "lucide-react";
import { API } from "@/App";

const statusColors = {
  pending: "bg-amber-100 text-amber-700",
  quote_pending: "bg-orange-100 text-orange-700",
  quote_required: "bg-purple-100 text-purple-700",
  accepted: "bg-blue-100 text-blue-700",
  scheduled: "bg-indigo-100 text-indigo-700",
  completed: "bg-green-100 text-green-700",
  contractor_assigned: "bg-purple-100 text-purple-700",
  approved: "bg-green-100 text-green-700"
};

const EPC_COLORS = {
  'A': 'bg-green-600',
  'B': 'bg-green-500',
  'C': 'bg-lime-500',
  'D': 'bg-yellow-500',
  'E': 'bg-orange-500',
  'F': 'bg-orange-600',
  'G': 'bg-red-600'
};

export const LandlordDashboard = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [user, setUser] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [jobs, setJobs] = useState([]);
  const [properties, setProperties] = useState([]);
  const [statements, setStatements] = useState([]);
  const [complianceAlerts, setComplianceAlerts] = useState([]);
  const [financialSummary, setFinancialSummary] = useState({
    total_rent_collected: 0,
    total_outstanding: 0,
    total_fees: 0,
    net_income: 0
  });
  
  // Settings states
  const [settings, setSettings] = useState({ auto_approval_threshold: 100 });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [thresholdInput, setThresholdInput] = useState("100");
  const [actionLoading, setActionLoading] = useState(null);
  
  // Payment method selection modal
  const [paymentModal, setPaymentModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("pay_direct");

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      
      const [jobsRes, propertiesRes, statementsRes, financialRes, complianceRes, settingsRes] = await Promise.all([
        axios.get(`${API}/landlord/jobs`, { headers }).catch(() => ({ data: { jobs: [] } })),
        axios.get(`${API}/landlord/properties`, { headers }).catch(() => ({ data: { properties: [] } })),
        axios.get(`${API}/landlord/statements`, { headers }).catch(() => ({ data: { statements: [] } })),
        axios.get(`${API}/landlord/financial-summary`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/landlord/compliance-alerts`, { headers }).catch(() => ({ data: { alerts: [] } })),
        axios.get(`${API}/landlord/settings`, { headers }).catch(() => ({ data: { settings: { auto_approval_threshold: 100 } } }))
      ]);
      
      setJobs(jobsRes.data?.jobs || []);
      setProperties(propertiesRes.data?.properties || []);
      setStatements(statementsRes.data?.statements || []);
      setFinancialSummary(financialRes.data || {});
      setComplianceAlerts(complianceRes.data?.alerts || []);
      
      const threshold = settingsRes.data?.settings?.auto_approval_threshold || 100;
      setSettings(settingsRes.data?.settings || { auto_approval_threshold: 100 });
      setThresholdInput(threshold.toString());
    } catch (error) {
      console.error("Error fetching data:", error);
      if (error.response?.status === 401) {
        localStorage.clear();
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveThreshold = async () => {
    const threshold = parseFloat(thresholdInput);
    if (isNaN(threshold) || threshold < 0) {
      toast.error("Please enter a valid amount");
      return;
    }
    if (threshold > 10000) {
      toast.error("Maximum threshold is £10,000");
      return;
    }

    setSettingsLoading(true);
    try {
      await axios.patch(
        `${API}/landlord/settings`,
        { auto_approval_threshold: threshold },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSettings({ ...settings, auto_approval_threshold: threshold });
      toast.success(`Auto-approval threshold set to £${threshold.toFixed(2)}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update settings");
    } finally {
      setSettingsLoading(false);
    }
  };

  const handleApproveQuote = async (jobId, approved, paymentMethod = null) => {
    setActionLoading(jobId);
    try {
      let url = `${API}/jobs/${jobId}/approve-quote?approved=${approved}`;
      if (approved && paymentMethod) {
        url += `&payment_method=${paymentMethod}`;
      }
      await axios.post(url, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(approved ? "Quote approved - contractor can proceed" : "Quote declined");
      setPaymentModal(false);
      setSelectedJob(null);
      fetchAllData();
    } catch (error) {
      toast.error("Failed to update quote");
    } finally {
      setActionLoading(null);
    }
  };

  const openApprovalModal = (job) => {
    setSelectedJob(job);
    setSelectedPaymentMethod("pay_direct");
    setPaymentModal(true);
  };

  const confirmApproval = () => {
    if (selectedJob) {
      handleApproveQuote(selectedJob.job_id, true, selectedPaymentMethod);
    }
  };

  const handleDownloadStatement = async (statementId) => {
    try {
      const response = await axios.get(`${API}/landlord-statements/${statementId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `statement_${statementId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Statement downloaded");
    } catch (error) {
      toast.error("Failed to download statement");
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    toast.success("Logged out");
    navigate("/login");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const pendingApprovals = jobs.filter(j => j.status === "quote_required");
  const activeJobs = jobs.filter(j => !["completed", "quote_required"].includes(j.status));
  const completedJobs = jobs.filter(j => j.status === "completed");
  
  // Calculate totals
  const totalProperties = properties.length;
  const occupiedProperties = properties.filter(p => p.tenant_name || p.current_tenant).length;
  const totalMonthlyRent = properties.reduce((sum, p) => sum + (p.rent_amount || 0), 0);

  return (
    <div className="min-h-screen bg-black">
      {/* Header with logo */}
      <header className="bg-black border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <img 
              src="/goodacres-full-logo.jpg" 
              alt="Goodacres" 
              className="h-12 w-auto"
            />
            <div>
              <p className="text-sm text-white/50">Landlord Portal</p>
              <h1 className="text-xl font-semibold text-white">{user?.name || "Landlord"}</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="relative text-white/70 hover:text-white hover:bg-white/10">
              <Bell className="w-5 h-5" />
              {pendingApprovals.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {pendingApprovals.length}
                </span>
              )}
            </Button>
            <Button variant="ghost" size="icon" onClick={handleLogout} className="text-white/70 hover:text-white hover:bg-white/10">
              <LogOut className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Compliance Alerts Banner */}
        {complianceAlerts.length > 0 && (
          <Card className="mb-6 border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-amber-800">Compliance Alerts</p>
                  <div className="mt-2 space-y-1">
                    {complianceAlerts.slice(0, 3).map((alert, idx) => (
                      <p key={idx} className="text-sm text-amber-700">
                        • {alert.property_address}: {alert.type} {alert.status === 'expired' ? 'has expired' : `expires ${alert.expiry_date}`}
                      </p>
                    ))}
                  </div>
                  {complianceAlerts.length > 3 && (
                    <Button variant="link" className="p-0 h-auto text-amber-700" onClick={() => setActiveTab("compliance")}>
                      View all {complianceAlerts.length} alerts →
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-6 mb-6">
            <TabsTrigger value="overview" className="text-xs">
              <Home className="w-4 h-4 mr-1" /> Overview
            </TabsTrigger>
            <TabsTrigger value="properties" className="text-xs">
              <Building className="w-4 h-4 mr-1" /> Properties
            </TabsTrigger>
            <TabsTrigger value="maintenance" className="text-xs">
              <Wrench className="w-4 h-4 mr-1" /> Maintenance
              {pendingApprovals.length > 0 && (
                <Badge className="ml-1 bg-red-500 text-white text-xs">{pendingApprovals.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="statements" className="text-xs">
              <FileText className="w-4 h-4 mr-1" /> Statements
            </TabsTrigger>
            <TabsTrigger value="compliance" className="text-xs">
              <Shield className="w-4 h-4 mr-1" /> Compliance
              {complianceAlerts.length > 0 && (
                <Badge className="ml-1 bg-amber-500 text-white text-xs">{complianceAlerts.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="settings" className="text-xs">
              <Settings className="w-4 h-4 mr-1" /> Settings
            </TabsTrigger>
          </TabsList>

          {/* OVERVIEW TAB */}
          <TabsContent value="overview" className="space-y-6">
            {/* Financial Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-br from-emerald-50 to-white border-emerald-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-emerald-600 font-medium">Total Rent Collected</p>
                      <p className="text-2xl font-bold text-emerald-700">
                        £{(financialSummary.total_rent_collected || totalMonthlyRent).toLocaleString()}
                      </p>
                    </div>
                    <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-emerald-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-amber-50 to-white border-amber-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-amber-600 font-medium">Outstanding</p>
                      <p className="text-2xl font-bold text-amber-700">
                        £{(financialSummary.total_outstanding || 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                      <Clock className="w-5 h-5 text-amber-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-purple-600 font-medium">Management Fees</p>
                      <p className="text-2xl font-bold text-purple-700">
                        £{(financialSummary.total_fees || 0).toLocaleString()}
                      </p>
                    </div>
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                      <TrendingDown className="w-5 h-5 text-purple-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-blue-600 font-medium">Net Income</p>
                      <p className="text-2xl font-bold text-blue-700">
                        £{(financialSummary.net_income || (totalMonthlyRent - (financialSummary.total_fees || 0))).toLocaleString()}
                      </p>
                    </div>
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <PoundSterling className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Stats Row */}
            <div className="grid grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-slate-700">{totalProperties}</p>
                  <p className="text-sm text-muted-foreground">Properties</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-emerald-600">{occupiedProperties}</p>
                  <p className="text-sm text-muted-foreground">Occupied</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-purple-600">{pendingApprovals.length}</p>
                  <p className="text-sm text-muted-foreground">Pending Quotes</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-3xl font-bold text-amber-600">{activeJobs.length}</p>
                  <p className="text-sm text-muted-foreground">Active Jobs</p>
                </CardContent>
              </Card>
            </div>

            {/* Pending Quote Approvals */}
            {pendingApprovals.length > 0 && (
              <Card className="border-purple-200 bg-purple-50/30">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <PoundSterling className="w-5 h-5 text-purple-600" />
                    Quotes Awaiting Your Approval
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {pendingApprovals.map((job) => (
                    <div
                      key={job.job_id}
                      className="bg-white rounded-lg p-4 border border-purple-100"
                      data-testid={`approval-${job.job_id}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className="bg-purple-100 text-purple-700 text-base px-3 py-1">
                              Quote: £{job.quote_amount?.toFixed(2) || "0.00"}
                            </Badge>
                            <span className="text-sm text-muted-foreground">
                              Job #{job.job_id?.slice(-6)}
                            </span>
                          </div>
                          <p className="font-medium">{job.issue?.issue_type}</p>
                          <p className="text-sm text-muted-foreground">
                            {job.issue?.description?.substring(0, 100)}...
                          </p>
                          <p className="text-sm mt-1">Tenant: {job.tenant?.name}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleApproveQuote(job.job_id, false)}
                            disabled={actionLoading === job.job_id}
                          >
                            <ThumbsDown className="w-4 h-4 mr-1" />
                            Decline
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => openApprovalModal(job)}
                            disabled={actionLoading === job.job_id}
                          >
                            {actionLoading === job.job_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <ThumbsUp className="w-4 h-4 mr-1" />
                                Approve
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* My Documents Quick Link */}
            <Card 
              className="border-emerald-200 bg-emerald-50 cursor-pointer hover:bg-emerald-100 transition-colors"
              onClick={() => navigate("/my-documents")}
              data-testid="my-documents-card"
            >
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Shield className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-medium">My Documents</p>
                    <p className="text-sm text-muted-foreground">Upload ID & Proof of Address for AML verification</p>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-emerald-600" />
              </CardContent>
            </Card>

            {/* Recent Statements */}
            {statements.length > 0 && (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <FileText className="w-5 h-5 text-blue-600" />
                      Recent Statements
                    </CardTitle>
                    <Button variant="link" onClick={() => setActiveTab("statements")}>
                      View All →
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {statements.slice(0, 3).map((stmt) => (
                      <div key={stmt.statement_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium">{stmt.period || stmt.month}</p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(stmt.created_at).toLocaleDateString('en-GB')}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <p className="font-bold text-emerald-600">£{(stmt.net_amount || stmt.total || 0).toLocaleString()}</p>
                          <Button size="sm" variant="ghost" onClick={() => handleDownloadStatement(stmt.statement_id)}>
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Contact Agency */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Phone className="w-5 h-5 text-blue-600" />
                  Contact Your Agent
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-6">
                  <div className="flex-1">
                    <p className="font-medium">Goodacres Residential</p>
                    <p className="text-sm text-muted-foreground">Your dedicated lettings team</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" asChild>
                      <a href="tel:01234567890">
                        <Phone className="w-4 h-4 mr-1" /> Call
                      </a>
                    </Button>
                    <Button variant="outline" asChild>
                      <a href="mailto:lettings@goodacres.com">
                        <Mail className="w-4 h-4 mr-1" /> Email
                      </a>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* PROPERTIES TAB */}
          <TabsContent value="properties" className="space-y-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-semibold">My Properties</h2>
                <p className="text-sm text-muted-foreground">{totalProperties} properties • {occupiedProperties} occupied</p>
              </div>
            </div>

            {properties.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="py-12 text-center">
                  <Building className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-muted-foreground">No properties found</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {properties.map((property) => (
                  <Card key={property.property_id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="font-medium">{property.address || property.address_line1}</h3>
                          <p className="text-sm text-muted-foreground flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> {property.postcode}
                          </p>
                        </div>
                        <Badge className={property.tenant_name || property.current_tenant ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}>
                          {property.tenant_name || property.current_tenant ? "Occupied" : "Vacant"}
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-3 py-3 border-t border-b">
                        <div className="text-center">
                          <BedDouble className="w-4 h-4 mx-auto text-slate-400 mb-1" />
                          <p className="text-sm font-medium">{property.bedrooms || "-"}</p>
                          <p className="text-xs text-muted-foreground">Beds</p>
                        </div>
                        <div className="text-center">
                          <PoundSterling className="w-4 h-4 mx-auto text-emerald-500 mb-1" />
                          <p className="text-sm font-bold text-emerald-600">£{(property.rent_amount || 0).toLocaleString()}</p>
                          <p className="text-xs text-muted-foreground">Rent/m</p>
                        </div>
                        <div className="text-center">
                          <div className={`w-6 h-6 mx-auto rounded text-white text-xs flex items-center justify-center mb-1 ${EPC_COLORS[property.epc_rating_current] || 'bg-slate-300'}`}>
                            {property.epc_rating_current || "?"}
                          </div>
                          <p className="text-xs text-muted-foreground">EPC</p>
                        </div>
                      </div>
                      
                      {(property.tenant_name || property.current_tenant) && (
                        <div className="mt-3 flex items-center gap-2">
                          <User className="w-4 h-4 text-slate-400" />
                          <span className="text-sm">{property.tenant_name || property.current_tenant?.name}</span>
                        </div>
                      )}
                      
                      <div className="mt-3 flex items-center gap-2">
                        <Button size="sm" variant="outline" className="flex-1">
                          <Eye className="w-4 h-4 mr-1" /> View Details
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* MAINTENANCE TAB */}
          <TabsContent value="maintenance" className="space-y-6">
            {/* Pending Quote Approvals */}
            {pendingApprovals.length > 0 && (
              <Card className="border-purple-200 bg-purple-50/30">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <PoundSterling className="w-5 h-5 text-purple-600" />
                    Quotes Awaiting Approval ({pendingApprovals.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {pendingApprovals.map((job) => (
                    <div key={job.job_id} className="bg-white rounded-lg p-4 border border-purple-100">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className="bg-purple-100 text-purple-700 text-base px-3 py-1">
                              Quote: £{job.quote_amount?.toFixed(2) || "0.00"}
                            </Badge>
                            <span className="text-sm text-muted-foreground">
                              Job #{job.job_id?.slice(-6)}
                            </span>
                          </div>
                          <p className="font-medium">{job.issue?.issue_type}</p>
                          <p className="text-sm text-muted-foreground line-clamp-2">
                            {job.issue?.description}
                          </p>
                          <p className="text-sm mt-1">Tenant: {job.tenant?.name}</p>
                          {job.quote_description && (
                            <p className="text-sm text-muted-foreground mt-2 italic">
                              "{job.quote_description}"
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleApproveQuote(job.job_id, false)}
                            disabled={actionLoading === job.job_id}
                          >
                            <ThumbsDown className="w-4 h-4 mr-1" />
                            Decline
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => openApprovalModal(job)}
                            disabled={actionLoading === job.job_id}
                          >
                            {actionLoading === job.job_id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <ThumbsUp className="w-4 h-4 mr-1" />
                                Approve
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Active Jobs */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Wrench className="w-5 h-5 text-amber-600" />
                  Active Jobs ({activeJobs.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {activeJobs.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No active maintenance jobs</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {activeJobs.map((job) => (
                      <div key={job.job_id} className="p-4 border rounded-lg hover:bg-slate-50">
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className={statusColors[job.status] || "bg-slate-100"}>
                                {job.status?.replace(/_/g, " ")}
                              </Badge>
                              {job.is_emergency && (
                                <Badge className="bg-red-100 text-red-700">
                                  <AlertTriangle className="w-3 h-3 mr-1" /> Emergency
                                </Badge>
                              )}
                            </div>
                            <p className="font-medium">{job.issue?.issue_type}</p>
                            <p className="text-sm text-muted-foreground line-clamp-2">
                              {job.issue?.description}
                            </p>
                            <p className="text-sm mt-1">Tenant: {job.tenant?.name}</p>
                            {job.quote_amount && job.quote_approved && (
                              <p className="text-sm text-green-600 mt-1">
                                ✓ Quote approved: £{job.quote_amount.toFixed(2)}
                              </p>
                            )}
                          </div>
                          {job.scheduled_time && (
                            <div className="text-right">
                              <p className="text-sm font-medium text-primary">
                                {new Date(job.scheduled_time).toLocaleDateString()}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(job.scheduled_time).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Completed Jobs */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  Completed Jobs ({completedJobs.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {completedJobs.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No completed jobs yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {completedJobs.slice(0, 5).map((job) => (
                      <div key={job.job_id} className="p-4 border rounded-lg bg-green-50/30">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium">{job.issue?.issue_type}</p>
                            <p className="text-sm text-muted-foreground">
                              {job.issue?.description?.substring(0, 100)}...
                            </p>
                            {job.quote_amount && (
                              <p className="text-sm text-muted-foreground mt-1">
                                Cost: £{job.quote_amount.toFixed(2)}
                              </p>
                            )}
                            <p className="text-sm text-green-600 mt-1">
                              Completed {new Date(job.updated_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* STATEMENTS TAB */}
          <TabsContent value="statements" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  Landlord Statements
                </CardTitle>
                <CardDescription>View and download your monthly statements</CardDescription>
              </CardHeader>
              <CardContent>
                {statements.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No statements available</p>
                    <p className="text-sm mt-1">Statements are generated monthly</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b bg-slate-50">
                          <th className="text-left py-3 px-4 font-medium">Period</th>
                          <th className="text-left py-3 px-4 font-medium">Date</th>
                          <th className="text-right py-3 px-4 font-medium">Rent</th>
                          <th className="text-right py-3 px-4 font-medium">Fees</th>
                          <th className="text-right py-3 px-4 font-medium">Net Amount</th>
                          <th className="text-center py-3 px-4 font-medium">Status</th>
                          <th className="text-center py-3 px-4 font-medium">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {statements.map((stmt) => (
                          <tr key={stmt.statement_id} className="border-b hover:bg-slate-50">
                            <td className="py-3 px-4 font-medium">{stmt.period || stmt.month}</td>
                            <td className="py-3 px-4">{new Date(stmt.created_at).toLocaleDateString('en-GB')}</td>
                            <td className="py-3 px-4 text-right">£{(stmt.rent_amount || stmt.gross_amount || 0).toLocaleString()}</td>
                            <td className="py-3 px-4 text-right text-red-600">-£{(stmt.fees || stmt.deductions || 0).toLocaleString()}</td>
                            <td className="py-3 px-4 text-right font-bold text-emerald-600">£{(stmt.net_amount || stmt.total || 0).toLocaleString()}</td>
                            <td className="py-3 px-4 text-center">
                              <Badge variant={stmt.status === 'paid' ? 'default' : 'secondary'}>
                                {stmt.status || 'pending'}
                              </Badge>
                            </td>
                            <td className="py-3 px-4 text-center">
                              <Button size="sm" variant="ghost" onClick={() => handleDownloadStatement(stmt.statement_id)}>
                                <Download className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* COMPLIANCE TAB */}
          <TabsContent value="compliance" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Shield className="w-5 h-5 text-emerald-600" />
                  Compliance Status
                </CardTitle>
                <CardDescription>Track EPC, Gas Safety, and EICR certificates</CardDescription>
              </CardHeader>
              <CardContent>
                {properties.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Shield className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p>No properties to show compliance for</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b bg-slate-50">
                          <th className="text-left py-3 px-4 font-medium">Property</th>
                          <th className="text-center py-3 px-4 font-medium">EPC</th>
                          <th className="text-center py-3 px-4 font-medium">Gas Safety</th>
                          <th className="text-center py-3 px-4 font-medium">EICR</th>
                        </tr>
                      </thead>
                      <tbody>
                        {properties.map((property) => {
                          const epcExpired = property.epc_expiry && new Date(property.epc_expiry) < new Date();
                          const gasExpired = property.gas_safety_expiry && new Date(property.gas_safety_expiry) < new Date();
                          const eicrExpired = property.eicr_expiry && new Date(property.eicr_expiry) < new Date();
                          
                          return (
                            <tr key={property.property_id} className="border-b hover:bg-slate-50">
                              <td className="py-3 px-4">
                                <p className="font-medium">{property.address || property.address_line1}</p>
                                <p className="text-xs text-muted-foreground">{property.postcode}</p>
                              </td>
                              <td className="py-3 px-4 text-center">
                                <div className="flex flex-col items-center">
                                  <div className={`w-8 h-8 rounded flex items-center justify-center text-white font-bold ${EPC_COLORS[property.epc_rating_current] || 'bg-slate-300'}`}>
                                    {property.epc_rating_current || "?"}
                                  </div>
                                  {property.epc_expiry && (
                                    <p className={`text-xs mt-1 ${epcExpired ? 'text-red-600 font-medium' : 'text-muted-foreground'}`}>
                                      {epcExpired ? 'Expired' : new Date(property.epc_expiry).toLocaleDateString('en-GB')}
                                    </p>
                                  )}
                                </div>
                              </td>
                              <td className="py-3 px-4 text-center">
                                {property.gas_safety_exempt ? (
                                  <Badge variant="secondary">Exempt</Badge>
                                ) : property.gas_safety_expiry ? (
                                  <div>
                                    <Badge className={gasExpired ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}>
                                      {gasExpired ? "Expired" : "Valid"}
                                    </Badge>
                                    <p className={`text-xs mt-1 ${gasExpired ? 'text-red-600' : 'text-muted-foreground'}`}>
                                      {new Date(property.gas_safety_expiry).toLocaleDateString('en-GB')}
                                    </p>
                                  </div>
                                ) : (
                                  <Badge variant="outline">Not Set</Badge>
                                )}
                              </td>
                              <td className="py-3 px-4 text-center">
                                {property.eicr_expiry ? (
                                  <div>
                                    <Badge className={eicrExpired ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}>
                                      {eicrExpired ? "Expired" : "Valid"}
                                    </Badge>
                                    <p className={`text-xs mt-1 ${eicrExpired ? 'text-red-600' : 'text-muted-foreground'}`}>
                                      {new Date(property.eicr_expiry).toLocaleDateString('en-GB')}
                                    </p>
                                  </div>
                                ) : (
                                  <Badge variant="outline">Not Set</Badge>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Compliance Alerts */}
            {complianceAlerts.length > 0 && (
              <Card className="border-amber-200">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2 text-amber-700">
                    <AlertTriangle className="w-5 h-5" />
                    Compliance Alerts ({complianceAlerts.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {complianceAlerts.map((alert, idx) => (
                      <div key={idx} className={`p-3 rounded-lg ${alert.status === 'expired' ? 'bg-red-50 border border-red-200' : 'bg-amber-50 border border-amber-200'}`}>
                        <div className="flex items-start gap-3">
                          <AlertCircle className={`w-5 h-5 ${alert.status === 'expired' ? 'text-red-600' : 'text-amber-600'}`} />
                          <div>
                            <p className="font-medium">{alert.property_address}</p>
                            <p className={`text-sm ${alert.status === 'expired' ? 'text-red-700' : 'text-amber-700'}`}>
                              {alert.type} {alert.status === 'expired' ? 'has expired' : `expires on ${alert.expiry_date}`}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* SETTINGS TAB */}
          <TabsContent value="settings" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <PoundSterling className="w-5 h-5" />
                  Auto-Approval Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>How it works:</strong> Quotes below your threshold are automatically 
                    approved, allowing contractors to proceed immediately. You'll receive a notification 
                    for your records, but no action is needed.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="threshold" className="text-base">
                    Auto-approve quotes under:
                  </Label>
                  <div className="flex items-center gap-3">
                    <div className="relative flex-1 max-w-xs">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">£</span>
                      <Input
                        id="threshold"
                        type="number"
                        min="0"
                        max="10000"
                        step="10"
                        value={thresholdInput}
                        onChange={(e) => setThresholdInput(e.target.value)}
                        className="pl-7 text-lg"
                        data-testid="auto-approval-threshold-input"
                      />
                    </div>
                    <Button
                      onClick={handleSaveThreshold}
                      disabled={settingsLoading}
                      data-testid="save-threshold-btn"
                    >
                      {settingsLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Save
                        </>
                      )}
                    </Button>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Current threshold: <strong>£{settings.auto_approval_threshold?.toFixed(2) || "100.00"}</strong>
                  </p>
                </div>

                <div className="pt-4 border-t">
                  <h4 className="font-medium mb-2">Quick presets:</h4>
                  <div className="flex flex-wrap gap-2">
                    {[50, 100, 150, 200, 500].map((amount) => (
                      <Button
                        key={amount}
                        variant="outline"
                        size="sm"
                        onClick={() => setThresholdInput(amount.toString())}
                        className={thresholdInput === amount.toString() ? "border-primary bg-primary/5" : ""}
                      >
                        £{amount}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
                  <p className="text-sm text-amber-800">
                    <strong>Note:</strong> Set to £0 to require manual approval for all quotes. 
                    Emergency repairs bypass this threshold and proceed immediately.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Payment Method Modal */}
      <Dialog open={paymentModal} onOpenChange={setPaymentModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Quote & Select Payment Method</DialogTitle>
            <DialogDescription>
              Choose how you'd like to pay for this work
            </DialogDescription>
          </DialogHeader>

          {selectedJob && (
            <div className="space-y-4 py-4">
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm text-muted-foreground mb-1">Quote Amount</p>
                <p className="text-2xl font-bold text-primary">
                  £{selectedJob.quote_amount?.toFixed(2) || "0.00"}
                </p>
                {selectedJob.quote_description && (
                  <p className="text-sm text-muted-foreground mt-2">
                    "{selectedJob.quote_description}"
                  </p>
                )}
              </div>

              <div className="space-y-3">
                <Label className="text-base font-medium">Payment Method</Label>
                
                <div 
                  className={`border rounded-lg p-4 cursor-pointer transition-all ${
                    selectedPaymentMethod === "deduct_from_rent" 
                      ? "border-primary bg-primary/5" 
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                  onClick={() => setSelectedPaymentMethod("deduct_from_rent")}
                  data-testid="payment-deduct-rent"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      selectedPaymentMethod === "deduct_from_rent" ? "bg-primary/10" : "bg-slate-100"
                    }`}>
                      <Banknote className={`w-5 h-5 ${
                        selectedPaymentMethod === "deduct_from_rent" ? "text-primary" : "text-slate-500"
                      }`} />
                    </div>
                    <div>
                      <p className="font-medium">Deduct from Next Rent</p>
                      <p className="text-sm text-muted-foreground">
                        Amount will be deducted from your next rent statement
                      </p>
                    </div>
                  </div>
                </div>

                <div 
                  className={`border rounded-lg p-4 cursor-pointer transition-all ${
                    selectedPaymentMethod === "pay_direct" 
                      ? "border-primary bg-primary/5" 
                      : "border-slate-200 hover:border-slate-300"
                  }`}
                  onClick={() => setSelectedPaymentMethod("pay_direct")}
                  data-testid="payment-direct"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      selectedPaymentMethod === "pay_direct" ? "bg-primary/10" : "bg-slate-100"
                    }`}>
                      <CreditCard className={`w-5 h-5 ${
                        selectedPaymentMethod === "pay_direct" ? "text-primary" : "text-slate-500"
                      }`} />
                    </div>
                    <div>
                      <p className="font-medium">Pay Directly</p>
                      <p className="text-sm text-muted-foreground">
                        Pay via bank transfer or card when invoiced
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentModal(false)}>
              Cancel
            </Button>
            <Button 
              onClick={confirmApproval} 
              disabled={actionLoading}
              data-testid="confirm-approve-btn"
            >
              {actionLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <ThumbsUp className="w-4 h-4 mr-2" />
              )}
              Approve Quote
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
